<template>
	<div class="card shadow-sm mb-2" @mouseover="hover = true" @mouseleave="hover = false" :class="{ shadow: hover }"
		@click="onClickItem(item)">
		<div class="card-body p-1" :class="!item.isViewed ? 'unread-color' : ''">
			<div class="form-row">
				<div class="col-md-2 pt-3 pl-3">
					<img :src="iconRenderer(item.type)" alt width="45" />
				</div>
				<div class="col-md-10 pt-2">
					<div class="row chat-header">
						<div class="col-md-12 text-left">
							<h6>{{ item.title }}</h6>
						</div>
					</div>
                    <div v-if="!discrepancy && !resolvedDispatch" class="row chat-body">
						<div class="col-md-12">{{ item.content }}</div>
					</div>
                    <div v-if="discrepancy" class="row chat-body">
                        <div class="col-md-12">
                            {{ discrepancy.content }}
                            <b-link @click="redirectDiscrepancyReport(discrepancy)" class="pl-0">
                                Click here to view and take action.
                            </b-link>
                        </div>
                    </div>
                    <div v-if="resolvedDispatch" class="row chat-body">
                        <div class="col-md-12">
                            {{ resolvedDispatch.content }}
                            <b-link @click="redirectDiscrepancyReport(resolvedDispatch)" class="pl-0">
                                Tap to view.
                            </b-link>
                        </div>
                    </div>
					<div v-if="dispatchId" class="row chat-body">
						<div class="col-md-12">
							<b-link @click="openDispatchDetails(item)" class="pl-0">See Details of {{ dispatchId }}</b-link>
						</div>
					</div>
					<div v-if="incidentId" class="row chat-body">
						<div class="col-md-12">
							<b-link @click="openIncidentDetails(item)" class="pl-0">See Details of {{ incidentId }}</b-link>
						</div>
					</div>
					<div class="row chat-footer">
						<div class="col-md-12 text-right" style="color: #999b9e">
							<time-ago :refresh="60" :datetime="dateSent" locale="en" :tooltip="true"
								:style="{ 'font-size': '8px' }" long />
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// Component
import dispatch from '@/assets/images/notifications/dispatch.svg';
import inTransit from '@/assets/images/notifications/in-transit.svg';
import receive from '@/assets/images/notifications/receive.svg';
import welcome from '@/assets/images/notifications/welcome.svg';
import locked from '@/assets/images/notifications/locked.svg';
import changeLocation from '@/assets/images/notifications/change-location.svg';
import incidentReport from '@/assets/images/notifications/incident-report.svg';
import approve from '@/assets/images/notifications/approve.svg';
import warning from '@/assets/images/notifications/warning.svg';
import warningHexagon from '@/assets/images/notifications/warning-hexagon.svg';
import meterPlus from '@/assets/images/notifications/meter-plus.svg';
import meterMinus from '@/assets/images/notifications/meter-minus.svg';

// DAO
import dispatchDAO from '@/database/dispatches';
import incidentReportDAO from '@/database/incidentReports';
import notificationDAO from '@/database/notifications';

// Util
import { DateUtil } from '@/utils/dateutil';

// Others
import config from '@/config/env-constants';
import EventBus from '@/shared/event-bus';
import TimeAgo from 'vue2-timeago';


export default {
	name: 'NotificationItem',
	components: { TimeAgo },
	data() {
		return {
			hover: false,
			icons: {
				dispatch,
				inTransit,
				receive,
				welcome,
				locked,
				changeLocation,
				incidentReport,
                approve,
                warning,
                warningHexagon,
                meterPlus,
                meterMinus
			},
		};
	},
	props: {
		item: {
			type: Object,
			required: true,
		},
	},
	computed: {
		dateSent() {
			return DateUtil.getFormattedDateWithTime(this.item.dateSent);
		},
		dispatchId() {
			const item = this.item ? this.item : {};
			return item.meta && item.meta.dispatchId ? item.meta.dispatchId : null;
		},
		incidentId() {
			const item = this.item ? this.item : {};
			return item.meta && item.meta.incidentId ? item.meta.incidentId : null;
		},
        discrepancy() {
            const item = this.item ? this.item : {};
            return item.type && item.type === config.notificationType.DISCREPANCY ? { ...item } : null;
        },
        resolvedDispatch() {
            const item = this.item ? this.item : {};
            return item.type && item.type === config.notificationType.RESOLVED_DISCREPANCY_REPORT ? { ...item } : null;
        },
	},
	methods: {
		async onClickItem(item) {
			try {
				if (item && !item.isViewed) {
					await notificationDAO.setAsViewed(item.id);
				}
			} catch (error) {
				this.$toaster.error('Error loading data. Please reload the page again.');
			}
		},
		iconRenderer(type) {
			let icon;
			if (type === config.notificationType.NEW_COMPANY) {
				icon = this.icons.welcome;
			} else if (type === config.notificationType.DISPATCH_DEPLOYED) {
				icon = this.icons.dispatch;
			} else if (type === config.notificationType.DISPATCH_IN_TRANSIT) {
				icon = this.icons.inTransit;
			} else if (type === config.notificationType.DISPATCH_RECEIVED) {
				icon = this.icons.receive;
			} else if (type === config.notificationType.LOCKED_ACCOUNT) {
				icon = this.icons.locked;
			} else if (type === config.notificationType.UNLOCKED_ACCOUNT) {
				icon = this.icons.welcome;
			} else if (type === config.notificationType.CHANGE_LOCATION) {
				icon = this.icons.changeLocation;
			} else if (type === config.notificationType.INCIDENT_REPORT) {
				icon = this.icons.incidentReport;
            } else if (type === config.notificationType.DISCREPANCY) {
                icon = this.icons.warning;
            } else if (type === config.notificationType.RESOLVED_DISCREPANCY_REPORT) {
                icon = this.icons.approve;
            } else if (type === config.notificationType.CONTRACT_LIMIT_EXCEEDED) {
                icon = this.icons.warningHexagon;
            } else if (type === config.notificationType.MAX_LIMIT_EXCEEDED) {
                icon = this.icons.meterPlus;
            } else if (type === config.notificationType.MIN_LIMIT_EXCEEDED) {
                icon = this.icons.meterMinus;
			} else {
				// Default mail icon
				icon = 'https://images.clipartlogo.com/files/istock/previews/7702/77021021-email-colored-vector-icon.jpg';
			}

			return icon;
		},
		async openDispatchDetails(notification) {
			const dispatchId = notification.meta ? notification.meta.dispatchId : null;

			if (dispatchId) {
				let dispatchObj = await dispatchDAO.getDispatchByDispatchId(dispatchId);
				EventBus.$emit('onShowDispatchDetails', dispatchObj);
			}
		},

		async openIncidentDetails(notification) {
			const incidentId = notification.meta ? notification.meta.incidentId : null;

			if (incidentId) {
				let incidentReportObj = await incidentReportDAO.getIncidentReportByIncidentId(incidentId);
				EventBus.$emit('onShowIncidentReportDetails', incidentReportObj);
			}
		},
        redirectDiscrepancyReport(item) {
            const { type, meta: { dispatchNo, dateDeployed } } = item;

            const status = type === config.notificationType.DISCREPANCY ? 'Pending' : 'Resolved';

            const url = this.$store.getters.isSuperAdmin
                ? "/admin/admin-discrepancy-reports"
                : "/discrepancy-reports";

            this.$router.replace(`${url}/${dispatchNo}?dateDeployed=${dateDeployed}&status=${status}`);
        },
	},
};
</script>

<style scoped>
.card {
	width: 376px;
	border: none;
	cursor: pointer;
}

.chat-header {
	padding: 4px 10px;
}

.chat-body {
	padding: 0px 10px 0px 10px;
}

.chat-footer {
	padding: 5px 10px;
}

.unread-color {
	background-color: #e7edf8;
}
</style>